import { buildApiUrl, parseErrorResponse } from './apiRequest';
import { csrfHeaders } from './csrf';
import { shellRequestHeaders } from './shellRequestHeaders';

const BASE_URL = window.location.origin;

type RequestHeaders = Record<string, string> | null;

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type FormMethod = 'POST' | 'PUT' | 'PATCH';

class RecruitApiClient {
  // Every request is same-origin, so a request that changes data carries the page's CSRF
  // token; headers the caller passes still win. A download does not ask for JSON.
  // eslint-disable-next-line class-methods-use-this
  getHeaders(headers: RequestHeaders, method: RequestMethod = 'GET', json = true) {
    return {
      'Content-Type': 'application/json',
      ...(method === 'GET' ? {} : csrfHeaders()),
      ...shellRequestHeaders(json),
      ...headers
    };
  }

  // Keep API errors as plain server payload objects; existing consumers depend on that shape.
  // eslint-disable-next-line class-methods-use-this
  handleResponse = async <T>(
    response: Response
  ): Promise<{ res: T; resHead: Record<string, string> }> => {
    if (response.ok) {
      const resHead: Record<string, string> = {};
      [...response.headers].forEach(([key, value]) => {
        Object.defineProperty(resHead, key, {
          enumerable: true,
          value
        });
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as T) : (null as T);
      return { res: data, resHead };
    }
    // The parsed payload keeps RecruitApiClient's thrown shape; typing it unknown satisfies only-throw-error.
    const error: unknown = await parseErrorResponse(response);
    throw error;
  };

  get = async <T>(
    path: string,
    params: unknown = null,
    headers: RequestHeaders = null,
    download?: boolean
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${buildApiUrl(path, params)}`, {
      headers: this.getHeaders(headers, 'GET', !download)
    }).then(async (r) => {
      if (download) return r.blob() as unknown as { res: T; resHead: Record<string, string> };
      return this.handleResponse<T>(r);
    });

  post = async <T>(
    path: string,
    params = {},
    headers: RequestHeaders = null,
    download?: boolean
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers, 'POST', !download),
      method: 'POST',
      body: JSON.stringify(params)
    }).then(async (r) => {
      if (download) return r.blob() as unknown as { res: T; resHead: Record<string, string> };
      return this.handleResponse<T>(r);
    });

  // Uploads: the browser sets the multipart Content-Type and its boundary, so none is sent.
  postForm = async <T>(
    path: string,
    formData: FormData,
    method: FormMethod = 'POST'
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${path}`, {
      headers: { ...csrfHeaders(), ...shellRequestHeaders(true) },
      method,
      body: formData
    }).then(async (r) => this.handleResponse<T>(r));

  put = async <T>(
    path: string,
    params = {},
    headers: RequestHeaders = null
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers, 'PUT'),
      method: 'PUT',
      body: JSON.stringify(params)
    }).then(async (r) => this.handleResponse<T>(r));

  patch = async <T>(
    path: string,
    params = {},
    headers: RequestHeaders = null
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers, 'PATCH'),
      method: 'PATCH',
      body: JSON.stringify(params)
    }).then(async (r) => this.handleResponse<T>(r));

  delete = async <T>(
    path: string,
    params = {},
    headers: RequestHeaders = null
  ): Promise<{ res: T; resHead: Record<string, string> }> =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers, 'DELETE'),
      method: 'DELETE',
      body: JSON.stringify(params)
    }).then(async (r) => this.handleResponse<T>(r));
}

// eslint-disable-next-line import-x/no-anonymous-default-export
export default new RecruitApiClient();
